// Port of SuiteModel/Maps.swift's writes (`extension FragmentDocument`): a map's notes, lines, regions and strokes,
// changed inside one Automerge change, key for key and type for type as the Swift writes them. Defaults are left out
// (no outline, normal size, not bold, the serif face, solid lines), so a stored note is as small as it can be; places
// are "x y" strings to a tenth of a point (`MapNumber`), one value per note, so two devices moving one note never mix
// their x and y; a note's words and a label are text objects, so two people typing in one merge.
import type * as A from '@automerge/automerge';
import { ROOT, SwiftReader, type ObjID } from '../automerge/swiftDoc.ts';
import { ModelError } from '../edit/modelError.ts';
import { bool, sameScalar, str, type Scalar, type Writer } from '../edit/typed.ts';
import { swiftCompare } from '../model/order.ts';
import {
  lineJoins, MapKey, MapLink, MapNumber, readMapContent, type MapArrow, type MapContent, type MapFace, type MapLineProperty, type MapNote,
  type MapNoteProperty, type MapPoint, type MapRegion, type MapStroke,
} from './map.ts';

type Backend = ReturnType<typeof A.getBackend>;

/** `FragmentDocument.create` for a map: the map's own maps, made with the document (never on first use, so two devices
 *  editing a new map write into one set), and Include in Compile off. Inside the document's first change. */
export function writeMapKeys(w: Writer) {
  for (const key of [MapKey.notes, MapKey.lines, MapKey.regions, MapKey.strokes]) w.ensure([MapKey.map, key]);
  w.put([], 'includeInCompile', bool(false));
}

const sortedIDs = (ids: Iterable<string>) => [...new Set(ids)].sort(swiftCompare);

/** A map's document inside a change (`d`, the change's root proxy). `makeID` names new lines (`IDs.make()`). */
export class MapDocument {
  readonly r: SwiftReader;
  private readonly raw: Backend;

  constructor(d: Record<string, any>, private readonly makeID: () => string = () => crypto.randomUUID().toLowerCase()) {
    this.r = new SwiftReader(d as unknown as A.Doc<unknown>);
    this.raw = this.r.backend;
  }

  // MARK: Plumbing (AutomergeSupport.swift)

  private ensureMap(obj: ObjID, key: string): ObjID {
    return this.r.map(obj, key) ?? (this.raw.putObject(obj, key, {}) as ObjID);
  }

  /** The map's own object. Made with the document (`writeMapKeys`), so this only makes it for a document that lacks it. */
  private mapRoot(): ObjID {
    return this.ensureMap(ROOT, MapKey.map);
  }

  private mapPart(key: string): ObjID {
    return this.ensureMap(this.mapRoot(), key);
  }

  private existingMapPart(key: string): ObjID | undefined {
    const root = this.r.map(ROOT, MapKey.map);
    return root === undefined ? undefined : this.r.map(root, key);
  }

  private requireMap() {
    if (this.r.string(ROOT, 'kind') !== 'map') throw new ModelError('invalid', `“${this.r.string(ROOT, 'title') ?? ''}” isn't a map.`);
  }

  private put(obj: ObjID, key: string, value: Scalar) {
    const v = value.t === 'timestamp' ? new Date(value.v.getTime()) : value.v;
    this.raw.put(obj, key, v as never, value.t as never);
  }

  private putIfChanged(obj: ObjID, key: string, value: Scalar) {
    if (!sameScalar(this.r.get(obj, key), value)) this.put(obj, key, value);
  }

  private putOrDelete(obj: ObjID, key: string, value: Scalar | null) {
    if (value) this.putIfChanged(obj, key, value);
    else if (this.r.get(obj, key) !== null) this.raw.delete(obj, key);
  }

  /** `setText`: the key's text object (made if missing), updated by a minimal diff. */
  private setText(obj: ObjID, key: string, value: string) {
    const text = this.ensureText(obj, key);
    if (this.r.text(text) !== value) this.raw.updateText(text, value);
  }

  private ensureText(obj: ObjID, key: string): ObjID {
    return this.r.textObject(obj, key) ?? (this.raw.putObject(obj, key, '') as ObjID);
  }

  private has(obj: ObjID, key: string) {
    return this.r.get(obj, key) !== null;
  }

  /** What the map holds now (this change's writes included). */
  mapContent(): MapContent {
    return readMapContent(this.r);
  }

  // MARK: Notes

  /** Adds a note (its text a text object, so two people typing in it merge). */
  addMapNote(note: MapNote) {
    this.requireMap();
    const notes = this.mapPart(MapKey.notes);
    if (this.has(notes, note.id)) throw new ModelError('invalid', 'A note with that id is already there.');
    const obj = this.raw.putObject(notes, note.id, {}) as ObjID;
    const text = this.raw.putObject(obj, 'text', '') as ObjID;
    if (note.text !== '') this.raw.updateText(text, note.text);
    this.put(obj, 'at', str(MapNumber.pair(note.at.x, note.at.y)));
    this.put(obj, 'createdAt', { t: 'timestamp', v: note.createdAt });
    const properties: MapNoteProperty[] = [{ width: note.width }, { outline: note.outline }, { ink: note.ink }, { size: note.size },
      { bold: note.bold }, { face: note.face }, { link: note.link }, { stack: note.stack, order: note.stackOrder }];
    for (const p of properties) this.write(p, obj);
  }

  private noteObject(id: string): ObjID {
    const notes = this.existingMapPart(MapKey.notes);
    const obj = notes === undefined ? undefined : this.r.map(notes, id);
    if (obj === undefined) throw new ModelError('notFound', `note ${id}`);
    return obj;
  }

  /** Replaces a note's words with the smallest edit, so someone typing in it elsewhere keeps their place. */
  setMapNoteText(id: string, text: string) {
    this.setText(this.noteObject(id), 'text', text);
  }

  /** The note's text object, for an editor splicing into it directly. */
  mapNoteTextObject(id: string): ObjID {
    return this.ensureText(this.noteObject(id), 'text');
  }

  /** Puts notes at new places; one value per note, so two devices moving one note never mix their x and y. */
  moveMapNotes(places: ReadonlyMap<string, MapPoint> | Record<string, MapPoint>) {
    const all = places instanceof Map ? [...places] : Object.entries(places);
    for (const [id, point] of all.sort(([a], [b]) => swiftCompare(a, b))) {
      this.putIfChanged(this.noteObject(id), 'at', str(MapNumber.pair(point.x, point.y)));
    }
  }

  setMapNote(id: string, property: MapNoteProperty) {
    this.write(property, this.noteObject(id));
  }

  private write(p: MapNoteProperty, obj: ObjID) {
    if ('outline' in p) this.putOrDelete(obj, 'outline', p.outline === 'none' ? null : str(p.outline));
    else if ('ink' in p) this.putOrDelete(obj, 'ink', p.ink === null ? null : str(p.ink));
    else if ('size' in p) this.putOrDelete(obj, 'size', p.size === 'normal' ? null : str(p.size));
    else if ('bold' in p) this.putOrDelete(obj, 'bold', p.bold ? bool(true) : null);
    else if ('face' in p) this.putOrDelete(obj, 'face', p.face === 'serif' ? null : str(p.face));
    // To a tenth of a point (positive, so `Math.round` is Swift's away-from-zero).
    else if ('width' in p) this.putOrDelete(obj, 'width', p.width === null ? null : { t: 'f64', v: Math.round(Math.max(p.width, 1) * 10) / 10 });
    else if ('link' in p) this.putOrDelete(obj, 'link', p.link === null ? null : str(MapLink.stored(p.link)));
    else {
      this.putOrDelete(obj, 'stack', p.stack === null ? null : str(p.stack));
      this.putOrDelete(obj, 'order', p.stack === null || p.order === null ? null : str(p.order));
    }
  }

  /** Removes notes with the lines touching them; notes stacked under one are left standing alone, and a removed centre
   *  leaves the map without one. */
  removeMapNotes(ids: Iterable<string>) {
    const gone = new Set(ids);
    const notes = this.existingMapPart(MapKey.notes);
    if (!gone.size || notes === undefined) return;
    for (const id of sortedIDs(gone)) if (this.has(notes, id)) this.raw.delete(notes, id);
    for (const [, [t, o]] of this.r.entries(notes)) {
      if (t !== 'map') continue;
      const parent = this.r.string(o as ObjID, 'stack');
      if (parent !== undefined && gone.has(parent)) this.write({ stack: null, order: null }, o as ObjID);
    }
    const lines = this.existingMapPart(MapKey.lines);
    if (lines !== undefined) {
      for (const [lineID, [t, o]] of this.r.entries(lines)) {
        if (t !== 'map') continue;
        const ends = [this.r.string(o as ObjID, 'from'), this.r.string(o as ObjID, 'to')];
        if (ends.some((end) => end === undefined || gone.has(end))) this.raw.delete(lines, lineID);
      }
    }
    const centre = this.r.string(this.mapRoot(), MapKey.centre);
    if (centre !== undefined && gone.has(centre)) this.setMapCentre(null);
  }

  // MARK: Lines

  /** Joins two notes; returns the line's id (the existing line's when they're already joined, either way round). */
  connectMapNotes(from: string, to: string, arrow: MapArrow = 'none', id: string = this.makeID()): string {
    if (from === to) throw new ModelError('invalid', "A note can't be joined to itself.");
    this.noteObject(from);
    this.noteObject(to);
    const existing = this.mapContent().lines.find((l) => lineJoins(l, from, to));
    if (existing) return existing.id;
    const obj = this.raw.putObject(this.mapPart(MapKey.lines), id, {}) as ObjID;
    this.put(obj, 'from', str(from));
    this.put(obj, 'to', str(to));
    if (arrow !== 'none') this.put(obj, 'arrow', str(arrow));
    return id;
  }

  setMapLine(id: string, property: MapLineProperty) {
    const lines = this.existingMapPart(MapKey.lines);
    const obj = lines === undefined ? undefined : this.r.map(lines, id);
    if (obj === undefined) throw new ModelError('notFound', `line ${id}`);
    if ('arrow' in property) this.putOrDelete(obj, 'arrow', property.arrow === 'none' ? null : str(property.arrow));
    else if ('ink' in property) this.putOrDelete(obj, 'ink', property.ink === null ? null : str(property.ink));
    else if ('style' in property) this.putOrDelete(obj, 'style', property.style === 'solid' ? null : str(property.style));
    else {
      if (property.label === '' && !this.has(obj, 'label')) return;
      this.setText(obj, 'label', property.label);
    }
  }

  removeMapLines(ids: Iterable<string>) {
    const lines = this.existingMapPart(MapKey.lines);
    if (lines === undefined) return;
    for (const id of sortedIDs(ids)) if (this.has(lines, id)) this.raw.delete(lines, id);
  }

  /** Takes out lines whose notes are gone (deleted on another device while this one joined them) and repeats of a line
   *  joining the same two notes: what readers already leave out, removed at the next edit. Returns how many. */
  tidyMapLines(): number {
    const lines = this.existingMapPart(MapKey.lines);
    if (lines === undefined) return 0;
    const kept = new Set(this.mapContent().lines.map((l) => l.id));
    let removed = 0;
    for (const id of this.r.keys(lines)) {
      if (kept.has(id)) continue;
      this.raw.delete(lines, id);
      removed += 1;
    }
    return removed;
  }

  // MARK: Regions and strokes

  addMapRegion(region: MapRegion) {
    this.requireMap();
    const obj = this.raw.putObject(this.mapPart(MapKey.regions), region.id, {}) as ObjID;
    this.writeRegion(region, obj);
  }

  updateMapRegion(region: MapRegion) {
    const regions = this.existingMapPart(MapKey.regions);
    const obj = regions === undefined ? undefined : this.r.map(regions, region.id);
    if (obj === undefined) throw new ModelError('notFound', `region ${region.id}`);
    this.writeRegion(region, obj);
  }

  private writeRegion(region: MapRegion, obj: ObjID) {
    this.putIfChanged(obj, 'at', str(MapNumber.pair(region.at.x, region.at.y)));
    this.putIfChanged(obj, 'size', str(MapNumber.pair(Math.max(region.width, 1), Math.max(region.height, 1))));
    this.putOrDelete(obj, 'ink', region.ink === null ? null : str(region.ink));
    if (region.label !== '' || this.has(obj, 'label')) this.setText(obj, 'label', region.label);
  }

  removeMapRegions(ids: Iterable<string>) {
    const regions = this.existingMapPart(MapKey.regions);
    if (regions === undefined) return;
    for (const id of sortedIDs(ids)) if (this.has(regions, id)) this.raw.delete(regions, id);
  }

  addMapStroke(stroke: MapStroke) {
    this.requireMap();
    if (!stroke.points.length) return;
    const obj = this.raw.putObject(this.mapPart(MapKey.strokes), stroke.id, {}) as ObjID;
    const list = this.raw.putObject(obj, 'points', []) as ObjID;
    stroke.points.forEach((p, i) => {
      const pressure = p.pressure === 1 ? '' : ' ' + MapNumber.format(p.pressure);
      this.raw.insert(list, i, MapNumber.pair(p.x, p.y) + pressure, 'str');
    });
    this.putOrDelete(obj, 'ink', stroke.ink === null ? null : str(stroke.ink));
    this.put(obj, 'width', { t: 'f64', v: stroke.width });
    if (stroke.offset.x !== 0 || stroke.offset.y !== 0) this.put(obj, 'offset', str(MapNumber.pair(stroke.offset.x, stroke.offset.y)));
  }

  /** Moves strokes: each one's whole offset from where it was drawn (zero leaves the key out). Throws for a stroke that
   *  isn't there. */
  moveMapStrokes(offsets: ReadonlyMap<string, MapPoint> | Record<string, MapPoint>) {
    const strokes = this.existingMapPart(MapKey.strokes);
    if (strokes === undefined) return;
    const all = offsets instanceof Map ? [...offsets] : Object.entries(offsets);
    for (const [id, offset] of all.sort(([a], [b]) => swiftCompare(a, b))) {
      const obj = this.r.map(strokes, id);
      if (obj === undefined) throw new ModelError('notFound', `stroke ${id}`);
      this.putOrDelete(obj, 'offset', offset.x === 0 && offset.y === 0 ? null : str(MapNumber.pair(offset.x, offset.y)));
    }
  }

  removeMapStrokes(ids: Iterable<string>) {
    const strokes = this.existingMapPart(MapKey.strokes);
    if (strokes === undefined) return;
    for (const id of sortedIDs(ids)) if (this.has(strokes, id)) this.raw.delete(strokes, id);
  }

  // MARK: The map as a whole

  /** Makes a note the centre (null: no centre). */
  setMapCentre(id: string | null) {
    if (id !== null) this.noteObject(id);
    this.putOrDelete(this.mapRoot(), MapKey.centre, id === null ? null : str(id));
  }

  setMapFace(face: MapFace) {
    this.requireMap();
    this.putOrDelete(this.mapRoot(), MapKey.face, face === 'serif' ? null : str(face));
  }

  /** Makes this map hold exactly `content` (a restore to a checkpoint, a copy): elements not in it are removed, the rest
   *  written over, so what didn't change makes no change. */
  replaceMapContent(content: MapContent) {
    this.requireMap();
    const current = this.mapContent();
    const wantedNotes = new Set(content.notes.map((n) => n.id));
    this.removeMapNotes(current.notes.map((n) => n.id).filter((id) => !wantedNotes.has(id)));
    const currentIDs = new Set(current.notes.map((n) => n.id));
    for (const note of content.notes) {
      if (currentIDs.has(note.id)) {
        this.setMapNoteText(note.id, note.text);
        this.moveMapNotes(new Map([[note.id, note.at]]));
        const properties: MapNoteProperty[] = [{ width: note.width }, { outline: note.outline }, { ink: note.ink }, { size: note.size },
          { bold: note.bold }, { face: note.face }, { link: note.link }, { stack: note.stack, order: note.stackOrder }];
        for (const p of properties) this.setMapNote(note.id, p);
      } else {
        this.addMapNote(note);
      }
    }
    const wantedLines = new Set(content.lines.map((l) => l.id));
    const existingLines = this.existingMapPart(MapKey.lines);
    if (existingLines !== undefined) for (const id of this.r.keys(existingLines)) if (!wantedLines.has(id)) this.raw.delete(existingLines, id);
    for (const line of content.lines) {
      const lines = this.mapPart(MapKey.lines);
      if (this.r.map(lines, line.id) === undefined) {
        const obj = this.raw.putObject(lines, line.id, {}) as ObjID;
        this.put(obj, 'from', str(line.from));
        this.put(obj, 'to', str(line.to));
      }
      this.setMapLine(line.id, { arrow: line.arrow });
      this.setMapLine(line.id, { ink: line.ink });
      this.setMapLine(line.id, { style: line.style });
      this.setMapLine(line.id, { label: line.label });
    }
    const wantedRegions = new Set(content.regions.map((r) => r.id));
    this.removeMapRegions(current.regions.map((r) => r.id).filter((id) => !wantedRegions.has(id)));
    for (const region of content.regions) {
      if (current.regions.some((r) => r.id === region.id)) this.updateMapRegion(region);
      else this.addMapRegion(region);
    }
    const wantedStrokes = new Set(content.strokes.map((s) => s.id));
    this.removeMapStrokes(current.strokes.map((s) => s.id).filter((id) => !wantedStrokes.has(id)));
    for (const stroke of content.strokes) {
      if (current.strokes.some((s) => s.id === stroke.id)) this.moveMapStrokes(new Map([[stroke.id, stroke.offset]]));
      else this.addMapStroke(stroke);
    }
    this.setMapCentre(content.centre);
    this.setMapFace(content.face);
  }
}
